import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, apiUpload, queryKeys } from '@/shared/api';

import type {
  DeletedDocument,
  DocumentAccessInfo,
  DocumentMedia,
  DocumentVersion,
  WorkspaceDocument,
} from './types';

export const documentCan = (document: Pick<WorkspaceDocument, 'my_permissions'>, action: string) =>
  document.my_permissions.includes(action);

export function useWorkspaceDocuments(workspaceId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.documents.list(workspaceId),
    queryFn: ({ signal }) =>
      api.get<WorkspaceDocument[]>(`/v1/workspaces/${workspaceId}/documents`, { signal }),
    enabled: enabled && workspaceId !== '',
  });
}

export function useMyDocuments() {
  return useQuery({
    queryKey: queryKeys.documents.mine,
    queryFn: ({ signal }) => api.get<WorkspaceDocument[]>('/v1/documents', { signal }),
  });
}

/** Ids of the documents whose name or TEXT matches: the catalog's own search only sees names. */
export function useDocumentSearch(workspaceId: string, query: string) {
  const text = query.trim();
  return useQuery({
    queryKey: queryKeys.documents.search(workspaceId, text),
    queryFn: ({ signal }) =>
      api.get<{ id: string }[]>(`/v1/workspaces/${workspaceId}/documents/search`, {
        signal,
        query: { q: text },
      }),
    enabled: text.length >= 2,
    staleTime: 15_000,
  });
}

export function useDocument(documentId: string) {
  return useQuery({
    queryKey: queryKeys.documents.detail(documentId),
    queryFn: ({ signal }) => api.get<WorkspaceDocument>(`/v1/documents/${documentId}`, { signal }),
  });
}

function useCatalogRefresh() {
  const client = useQueryClient();
  return () => client.invalidateQueries({ queryKey: queryKeys.documents.all });
}

export function useCreateDocument(workspaceId: string) {
  const refresh = useCatalogRefresh();
  return useMutation({
    mutationFn: (body: {
      name: string;
      section_id?: string | null;
      group_id?: string | null;
      team_access?: { team_id: string; role: string }[];
      role_access?: { subject_role?: string; custom_role_id?: string; role: string }[];
    }) => api.post<WorkspaceDocument>(`/v1/workspaces/${workspaceId}/documents`, body),
    onSuccess: () => refresh(),
  });
}

export function useImportDocument(workspaceId: string) {
  const refresh = useCatalogRefresh();
  return useMutation({
    mutationFn: ({
      file,
      name,
      section_id,
      group_id,
    }: {
      file: File;
      name: string;
      section_id?: string | null | undefined;
      group_id?: string | null | undefined;
    }) =>
      apiUpload<WorkspaceDocument>(`/v1/workspaces/${workspaceId}/documents/import`, file, {
        filename: file.name,
        query: { name, section_id: section_id ?? undefined, group_id: group_id ?? undefined },
      }),
    onSuccess: () => refresh(),
  });
}

export function useRenameDocument(documentId: string) {
  const refresh = useCatalogRefresh();
  return useMutation({
    mutationFn: (name: string) =>
      api.patch<WorkspaceDocument>(`/v1/documents/${documentId}`, { name }),
    onSuccess: () => refresh(),
  });
}

/** The workspace labels on a note: the full list, in display order. */
export function useSetDocumentLabels(documentId: string) {
  const refresh = useCatalogRefresh();
  return useMutation({
    mutationFn: (labelIds: string[]) =>
      api.put<WorkspaceDocument>(`/v1/documents/${documentId}/labels`, { label_ids: labelIds }),
    onSuccess: () => refresh(),
  });
}

export function useSetDocumentFavorite(document: WorkspaceDocument) {
  const refresh = useCatalogRefresh();
  return useMutation({
    mutationFn: (favorite: boolean) =>
      favorite
        ? api.put(`/v1/documents/${document.id}/favorite`)
        : api.delete(`/v1/documents/${document.id}/favorite`),
    onSettled: () => refresh(),
  });
}

export function useDuplicateDocument() {
  const refresh = useCatalogRefresh();
  return useMutation({
    mutationFn: (documentId: string) =>
      api.post<WorkspaceDocument>(`/v1/documents/${documentId}/duplicate`),
    onSuccess: () => refresh(),
  });
}

export function useTrashDocument() {
  const refresh = useCatalogRefresh();
  return useMutation({
    mutationFn: (documentId: string) => api.delete(`/v1/documents/${documentId}`),
    onSuccess: () => refresh(),
  });
}

export function useDeletedDocuments(workspaceId: string) {
  return useQuery({
    queryKey: queryKeys.documents.trash(workspaceId),
    queryFn: ({ signal }) =>
      api.get<DeletedDocument[]>(`/v1/workspaces/${workspaceId}/documents/trash`, { signal }),
  });
}

export function useRestoreDocument() {
  const refresh = useCatalogRefresh();
  return useMutation({
    mutationFn: (documentId: string) =>
      api.post<WorkspaceDocument>(`/v1/documents/${documentId}/restore`),
    onSuccess: () => refresh(),
  });
}

export function usePurgeDocument() {
  const refresh = useCatalogRefresh();
  return useMutation({
    mutationFn: (documentId: string) => api.delete(`/v1/documents/${documentId}/purge`),
    onSuccess: () => refresh(),
  });
}

export function uploadDocumentMedia(documentId: string, file: File) {
  return apiUpload<DocumentMedia>(`/v1/documents/${documentId}/media`, file, {
    filename: file.name,
  });
}

export const documentMediaUrl = (documentId: string, mediaId: string) =>
  `/api/v1/documents/${encodeURIComponent(documentId)}/media/${encodeURIComponent(mediaId)}`;

export const documentExportUrl = (documentId: string, format: 'md' | 'docx' | 'pdf') =>
  `/api/v1/documents/${encodeURIComponent(documentId)}/export?format=${format}`;

/** Appends to the document through the server's CRDT room: connected editors see it arrive. */
export function useAppendMarkdown(documentId: string) {
  return useMutation({
    mutationFn: (file: File) =>
      apiUpload<undefined>(`/v1/documents/${documentId}/import`, file, { filename: file.name }),
  });
}

export function useDocumentVersions(documentId: string) {
  return useQuery({
    queryKey: queryKeys.documents.versions(documentId),
    queryFn: ({ signal }) =>
      api.get<DocumentVersion[]>(`/v1/documents/${documentId}/versions`, { signal }),
  });
}

export function useCreateDocumentVersion(documentId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<DocumentVersion>(`/v1/documents/${documentId}/versions`),
    onSettled: () =>
      client.invalidateQueries({ queryKey: queryKeys.documents.versions(documentId) }),
  });
}

export function useRestoreDocumentVersion(documentId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (versionId: string) =>
      api.post(`/v1/documents/${documentId}/versions/${versionId}/restore`),
    onSettled: () =>
      client.invalidateQueries({ queryKey: queryKeys.documents.versions(documentId) }),
  });
}

export function useDocumentAccess(documentId: string) {
  return useQuery({
    queryKey: queryKeys.documents.access(documentId),
    queryFn: ({ signal }) =>
      api.get<DocumentAccessInfo>(`/v1/documents/${documentId}/access`, { signal }),
  });
}

/** Every access change answers with the whole panel. */
function useAccessMutation<T>(documentId: string, send: (input: T) => Promise<DocumentAccessInfo>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: send,
    onSuccess: (data) => {
      client.setQueryData(queryKeys.documents.access(documentId), data);
    },
  });
}

export const useSetDocumentMember = (documentId: string) =>
  useAccessMutation(documentId, ({ userId, role }: { userId: string; role: string }) =>
    api.put<DocumentAccessInfo>(`/v1/documents/${documentId}/members/${userId}`, { role }),
  );

export const useRemoveDocumentMember = (documentId: string) =>
  useAccessMutation(documentId, (userId: string) =>
    api.delete<DocumentAccessInfo>(`/v1/documents/${documentId}/members/${userId}`),
  );

export const useSetDocumentDenial = (documentId: string) =>
  useAccessMutation(documentId, ({ userId, denied }: { userId: string; denied: boolean }) =>
    denied
      ? api.put<DocumentAccessInfo>(`/v1/documents/${documentId}/denials/${userId}`)
      : api.delete<DocumentAccessInfo>(`/v1/documents/${documentId}/denials/${userId}`),
  );

export const useSetDocumentRule = (documentId: string) =>
  useAccessMutation(
    documentId,
    (body: {
      subject_kind: string;
      subject_role?: string;
      team_id?: string;
      custom_role_id?: string;
      role: string;
    }) => api.post<DocumentAccessInfo>(`/v1/documents/${documentId}/access-rules`, body),
  );

export const useDeleteDocumentRule = (documentId: string) =>
  useAccessMutation(documentId, (ruleId: string) =>
    api.delete<DocumentAccessInfo>(`/v1/documents/${documentId}/access-rules/${ruleId}`),
  );
